<div class="flex flex-col h-full min-h-0 py-2">
  <p class="text-sm text-muted-color mb-3 shrink-0">
    Assign each item in the list on the right to its corresponding CAD object.
    Click <strong>Link</strong> on an item, then select the matching CAD object
    from the tree.
  </p>
  <!-- 3-column layout: 3D Preview | CAD Objects | Items to Link -->
  <div class="flex gap-4 flex-1 min-h-0">
    <!-- Left 1/3: 3D Preview -->
    <div
      class="w-1/3 border border-surface-switch-300 rounded-lg overflow-hidden min-h-0"
    >
      <app-cad-viewer
        [cadObjects]="viewerCadObjects()"
        [visibleCadObjectIds]="visibleCadObjectIds()"
        [externallyHoveredCadObjectId]="hoveredCadObjectId()"
        (hoveredCadObjectIdChange)="hoveredCadObjectId.set($event)"
      />
    </div>

    <!-- Middle 1/3: CAD object tree -->
    <div
      class="w-1/3 border border-surface-switch-300 rounded-lg p-4 overflow-y-auto min-h-0"
    >
      <p class="font-semibold mb-3">CAD Objects</p>
      @if (isLinking() && checkedCadObjects().length === 0) {
        <p class="text-muted-color text-sm italic mb-3">
          Select the CAD objects to link to this item from the CAD tree.
        </p>
      }
      @for (cadObject of availableCadObjects(); track cadObject.id) {
        <ng-container
          *ngTemplateOutlet="cadTreeNode; context: { $implicit: cadObject }"
        />
      } @empty {
        <p class="text-muted-color text-sm italic p-2">
          No viewable CAD objects in this file.
        </p>
      }
    </div>

    <!-- Right 1/3: Items panel -->
    <div
      class="w-1/3 border border-surface-switch-300 rounded-lg p-4 overflow-y-auto flex flex-col min-h-0"
    >
      <p class="font-semibold mb-3">Items to Link</p>
      @for (node of linkTree(); track node.itemId) {
        <ng-container
          *ngTemplateOutlet="itemNode; context: { $implicit: node }"
        />
      } @empty {
        <p class="text-muted-color text-sm italic">No items available.</p>
      }
    </div>
  </div>

  @if (showUnlinkedWarning()) {
    <p-message severity="warn" styleClass="w-full mt-4">
      No items have any linked CAD objects. Click Save again to proceed anyway.
    </p-message>
  }

  <div class="flex justify-end gap-2 mt-4 shrink-0">
    <p-button label="Back" severity="secondary" (onClick)="onBack()" />
    <p-button
      [label]="showUnlinkedWarning() ? 'Save Anyway' : 'Save'"
      icon="pi pi-check"
      (onClick)="onSave()"
    />
  </div>
</div>

<!-- Recursive CAD tree node template -->
<ng-template #cadTreeNode let-cadObject>
  <div>
    <div
      [class.bg-surface-switch-200]="isHovered(cadObject)"
      (mouseenter)="setHover(cadObject, true)"
      (mouseleave)="setHover(cadObject, false)"
      class="flex gap-2 items-center py-1 px-1 rounded-md transition-colors"
    >
      <div class="flex items-center">
        @if (cadObject.children.length > 0) {
          <p-button
            [icon]="
              isExpanded(cadObject)
                ? 'pi pi-chevron-down'
                : 'pi pi-chevron-right'
            "
            [text]="true"
            size="small"
            (onClick)="toggleExpanded(cadObject)"
            aria-label="Toggle Expand"
          />
        }
        @if (isLinking()) {
          <p-checkbox
            [binary]="true"
            [ngModel]="isChecked(cadObject)"
            (onChange)="toggleChecked(cadObject)"
          />
        } @else if (isLinked(cadObject)) {
          <i
            class="pi pi-link text-primary text-sm"
            [pTooltip]="getLinkedTooltip(cadObject)"
            tooltipPosition="top"
          ></i>
        } @else {
          <i
            class="pi pi-file text-muted-color text-sm"
            pTooltip="Unlinked"
            tooltipPosition="top"
          ></i>
        }
      </div>

      <span class="flex-1">{{ cadObject.name }}</span>

      @if (!cadObject.children.length) {
        <p-button
          [icon]="
            isCadObjectVisible(cadObject) ? 'pi pi-eye' : 'pi pi-eye-slash'
          "
          [rounded]="true"
          [outlined]="true"
          severity="secondary"
          size="small"
          (onClick)="setVisibility(cadObject, !isCadObjectVisible(cadObject))"
          pTooltip="Toggle Visibility"
          tooltipPosition="right"
          aria-label="Toggle Visibility"
        />
      }
    </div>
    @if (isExpanded(cadObject) && cadObject.children.length > 0) {
      <div class="ms-4">
        @for (child of cadObject.children; track child.id) {
          <ng-container
            *ngTemplateOutlet="cadTreeNode; context: { $implicit: child }"
          />
        }
      </div>
    }
  </div>
</ng-template>

<!-- Recursive item node template -->
<ng-template #itemNode let-node>
  <div>
    <div
      class="flex items-center justify-between py-1"
      [class.opacity-50]="
        activelyLinkingItemId() !== null &&
        activelyLinkingItemId() !== node.itemId
      "
    >
      <div class="flex items-center gap-1 min-w-0 flex-1 mr-2">
        @if (node.children.length > 0) {
          <p-button
            [icon]="
              isItemExpanded(node.itemId)
                ? 'pi pi-chevron-down'
                : 'pi pi-chevron-right'
            "
            [text]="true"
            size="small"
            (onClick)="toggleItemExpanded(node.itemId)"
            aria-label="Toggle Expand"
          />
        } @else {
          <i
            class="pi pi-circle-fill text-primary pl-3 w-8 leading-none"
            style="font-size: 6px"
          ></i>
        }
        <span class="text-sm truncate">{{ node.name }}</span>
      </div>
      @if (activelyLinkingItemId() === node.itemId) {
        <div class="flex gap-1 shrink-0">
          <p-button
            label="Cancel"
            severity="secondary"
            size="small"
            (onClick)="cancelLinking()"
          />
          <p-button
            label="Save"
            icon="pi pi-check"
            size="small"
            (onClick)="saveLinking()"
          />
        </div>
      } @else {
        <p-button
          [label]="
            getItemCadObjectIds(node.itemId).length > 0 ? 'Re-link' : 'Link'
          "
          size="small"
          (onClick)="startLinking(node.itemId)"
          [style.visibility]="
            activelyLinkingItemId() !== null &&
            activelyLinkingItemId() !== node.itemId
              ? 'hidden'
              : 'visible'
          "
          [disabled]="
            activelyLinkingItemId() !== null &&
            activelyLinkingItemId() !== node.itemId
          "
        />
      }
    </div>

    @if (
      activelyLinkingItemId() !== node.itemId &&
      getItemLinkedCadObjects(node.itemId).length > 0
    ) {
      <div
        class="ml-8 mb-1"
        [class.opacity-50]="activelyLinkingItemId() !== null"
      >
        @for (cadObj of getItemLinkedCadObjects(node.itemId); track cadObj.id) {
          <div
            class="flex items-center p-2 rounded bg-surface-switch-100 mb-0.5"
          >
            <i class="pi pi-link text-xs mr-1 text-primary"></i>
            <span class="text-sm truncate">{{ cadObj.name }}</span>
          </div>
        }
      </div>
    }
    @if (activelyLinkingItemId() === node.itemId) {
      @if (checkedCadObjects().length > 0) {
        <div class="ml-8 mb-1">
          @for (cadObj of checkedCadObjects(); track cadObj.id) {
            <div
              class="flex items-center py-0.5 px-2 rounded bg-surface-switch-100 mb-0.5"
            >
              <span class="text-sm truncate">{{ cadObj.name }}</span>
            </div>
          }
        </div>
      }
    }

    @if (isItemExpanded(node.itemId) && node.children.length > 0) {
      <div class="ms-4">
        @for (child of node.children; track child.itemId) {
          <ng-container
            *ngTemplateOutlet="itemNode; context: { $implicit: child }"
          />
        }
      </div>
    }
  </div>
</ng-template>
